/**
 * Agent package search-card room SSOT.
 *
 * The cheapest occupancy-compatible room from the LDS matrix (shortenedDeal /
 * extended rows) is the only room identity used for card price, card name,
 * sort/filter, click persistence, and RoomsSelector initial selection.
 *
 * Cheapest-room is decided HERE once. Profile must not re-pick cheapest.
 */

import { getRoomsSetByCount } from "../LDSHelpers";
import { selectedRoomMatchesSearchOccupancy } from "../passengers/packageOccupancySignature";
import { normalizeLdsExtendedDealRows } from "./normalizeLdsExtendedDealRows";

export const SEARCH_CARD_ROOM_FIELD = "searchCardRoom";

function pickDealSeq(row) {
  if (!row || typeof row !== "object") return null;
  const seq = row["@seqNum"] ?? row.seqNum ?? row.dealSeq ?? row.roomId ?? row.RoomId;
  if (seq == null || String(seq).trim() === "") return null;
  return String(seq).trim();
}

function normalizeRoomTypeLabel(roomOrSuggestion) {
  const t =
    roomOrSuggestion?.roomType ??
    roomOrSuggestion?.RoomType ??
    roomOrSuggestion?.roomTypeName ??
    roomOrSuggestion?.RoomTypeName ??
    roomOrSuggestion?.roomName ??
    roomOrSuggestion?.RoomName ??
    "";
  const s = String(t).trim();
  return s ? s.toUpperCase() : null;
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toPosNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function pickStr(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

export function normalizeSearchCardBoard(value) {
  if (value == null || value === "") return null;
  const parts = String(value).trim().split(/\s+/).filter(Boolean);
  const raw = parts.length ? parts[parts.length - 1] : "";
  if (!raw) return null;
  return raw.toUpperCase();
}

export function normalizeOccupancyRooms(occupancyRooms) {
  const list = Array.isArray(occupancyRooms) ? occupancyRooms : [];
  return list.map((room) => {
    const rm = room && typeof room === "object" ? room : {};
    const adults = Math.max(0, Math.floor(toInt(rm.adults)));
    const kids = Math.max(0, Math.floor(toInt(rm.kids ?? rm.children)));
    const infants = Math.max(0, Math.floor(toInt(rm.infants ?? rm.babies)));
    return { adults, kids, infants };
  });
}

function occupancyCompatibleRoomSets(slot) {
  return getRoomsSetByCount(slot.adults, slot.kids);
}

function rowRoomSet(row) {
  return (
    pickStr(row, ["roomSet", "RoomSet", "room_set", "rset", "RSET"]) ||
    pickStr(row?.room, ["roomSet", "RoomSet", "room_set"])
  );
}

/** Parse LDS occupancy tokens like A1, A2, A2C1, P3. */
export function parseLdsRoomSetOccupancy(roomSet) {
  const token = String(roomSet || "").trim().toUpperCase();
  if (!token) return null;
  const a = token.match(/^A(\d+)(?:C(\d+))?$/);
  if (a) {
    return {
      kind: "A",
      adults: Number(a[1]),
      kids: Number(a[2] || 0),
    };
  }
  const p = token.match(/^P(\d+)$/);
  if (p) {
    return { kind: "P", adults: Number(p[1]), kids: 0 };
  }
  return null;
}

export function occupancySlotFromPaxInfo(pax) {
  if (!pax || typeof pax !== "object") return null;
  const count = (value) => {
    if (Array.isArray(value)) return value.length;
    return Math.max(0, Math.floor(toInt(value)));
  };
  return {
    adults: count(pax.adults),
    kids: count(pax.kids ?? pax.children),
    infants: count(pax.infants ?? pax.babies),
  };
}

function rowInfantFare(row) {
  const v = row?.infantFare ?? row?.InfantFare;
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function isRoomRowOccupancyCompatible(row, slot) {
  if (!row || !slot) return false;
  const roomSet = rowRoomSet(row);
  if (!roomSet) return false;
  const allowed = occupancyCompatibleRoomSets(slot);
  if (!Array.isArray(allowed) || allowed.length === 0) return false;
  const want = new Set(allowed.map((s) => String(s).trim().toUpperCase()).filter(Boolean));
  const got = roomSet.toUpperCase();
  if (!want.has(got)) return false;
  // Belt: A1 (1 adult) must never fill a 2-adult slot even if the allow-list is wrong.
  const parsed = parseLdsRoomSetOccupancy(got);
  if (parsed?.kind === "A" && parsed.adults < slot.adults) return false;
  if (slot.infants > 0 && !rowInfantFare(row)) return false;
  return true;
}

export function filterAvailableRoomsForSlotOccupancy(availableRooms, occupancySlot) {
  if (!Array.isArray(availableRooms)) return [];
  return availableRooms.filter((row) => {
    if (!row) return false;
    if (!occupancySlot) return true;
    return isRoomRowOccupancyCompatible(suggestionFromAvailableRow(row), occupancySlot);
  });
}

function rowTotalFare(row) {
  return toPosNumber(row?.totalFare ?? row?.TotalFare) ?? 0;
}

function rowAdultFare(row) {
  return toPosNumber(row?.adultFare ?? row?.AdultFare);
}

function rowRoomTypeDisplay(row) {
  return (
    pickStr(row, [
      "roomTypeName",
      "RoomTypeName",
      "roomType",
      "RoomType",
      "roomName",
      "RoomName",
      "boxName",
      "BoxName",
    ]) || null
  );
}

function rowBoard(row) {
  return normalizeSearchCardBoard(
    pickStr(row, ["board", "Board", "boardBasis", "BoardBasis", "boardType", "BoardType"])
  );
}

function collectMatrixRows({ fullDeal, shortenedDeal, extendedDeal }) {
  const chunks = [
    shortenedDeal,
    extendedDeal,
    fullDeal?.shortenedDeal,
    fullDeal?.extendedDeal,
    fullDeal?.extended,
  ];
  const rows = [];
  for (const chunk of chunks) {
    const list = normalizeLdsExtendedDealRows(chunk);
    if (list.length) rows.push(...list);
  }
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const seq = pickDealSeq(row) || "";
    const roomSet = rowRoomSet(row);
    const roomType = normalizeRoomTypeLabel(row) || "";
    const fare = String(rowTotalFare(row));
    const key = `${seq}|${roomSet}|${roomType}|${fare}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function pickCheapestCompatibleRow(rows, slot, usedSeqs = null) {
  const compatible = rows.filter((row) => {
    if (!isRoomRowOccupancyCompatible(row, slot)) return false;
    const seq = pickDealSeq(row);
    if (usedSeqs && seq && usedSeqs.has(seq)) return false;
    return true;
  });
  if (compatible.length === 0) return null;
  return [...compatible].sort((a, b) => {
    const fareDiff = rowTotalFare(a) - rowTotalFare(b);
    if (fareDiff !== 0) return fareDiff;
    return (rowAdultFare(a) || 0) - (rowAdultFare(b) || 0);
  })[0];
}

export function canSafelyPriceFromRoomMatrix(rows, occupancyRooms) {
  const slots = normalizeOccupancyRooms(occupancyRooms);
  if (slots.length === 0) return false;
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const allCovered = slots.every((slot) => rows.some((row) => isRoomRowOccupancyCompatible(row, slot)));
  if (!allCovered) return false;
  if (slots.length === 1) return true;
  const distinct = new Set();
  for (const row of rows) {
    const seq = pickDealSeq(row);
    if (seq) distinct.add(`seq:${seq}`);
    else {
      const type = normalizeRoomTypeLabel(row) || "";
      const set = rowRoomSet(row);
      if (type || set) distinct.add(`type:${type}|set:${set}`);
    }
  }
  return distinct.size >= slots.length || rows.length >= slots.length;
}

/**
 * True when searchLds did not supply a usable occupancy matrix (typical
 * single-room responses omit shortenedDeal). Canonical SKU then requires
 * findExtendedDeals before card/sort/filter can share one room-price object.
 */
export function packageSearchNeedsExtendedDealMatrix({
  fullDeal = null,
  shortenedDeal = null,
  extendedDeal = null,
  occupancyRooms = null,
} = {}) {
  const slots = normalizeOccupancyRooms(occupancyRooms);
  if (slots.length === 0) return false;
  const rows = collectMatrixRows({ fullDeal, shortenedDeal, extendedDeal });
  return !canSafelyPriceFromRoomMatrix(rows, slots);
}

function hotelIdFromDeal(fullDeal) {
  const hotel = fullDeal?.hotel;
  return (
    hotel?.hotelId ??
    hotel?.id ??
    hotel?.LdsId ??
    hotel?.hotelID ??
    fullDeal?.hotelId ??
    null
  );
}

function roomTypeFromHotelName(hotelName) {
  const name = String(hotelName || "").trim();
  if (!name || !/[–—-]/.test(name)) return null;
  const parts = name.split(/\s*[–—-]\s*/).filter(Boolean);
  if (parts.length < 2) return null;
  const suffix = parts[parts.length - 1].trim();
  return suffix || null;
}

function fallbackFromDealFare(fullDeal, occupancyRooms) {
  const hotel = fullDeal?.hotel || {};
  const dealFare = fullDeal?.dealFare || {};
  const slots = normalizeOccupancyRooms(occupancyRooms);
  const primary = slots[0] || { adults: 0, kids: 0, infants: 0 };
  const roomSets = occupancyCompatibleRoomSets(primary);
  const roomType =
    pickStr(hotel, ["roomType", "room_type", "roomTypeName", "RoomType"]) ||
    pickStr(fullDeal, ["roomType", "roomTypeName"]) ||
    roomTypeFromHotelName(hotel.hotelName || hotel.name) ||
    null;
  const seqNum =
    pickDealSeq(hotel) ||
    pickDealSeq(dealFare) ||
    pickDealSeq(fullDeal) ||
    null;
  return {
    seqNum,
    roomType,
    roomTypeName: roomType,
    board: normalizeSearchCardBoard(hotel.board),
    roomSet: Array.isArray(roomSets) && roomSets.length ? roomSets[0] : null,
    adultFare: rowAdultFare(dealFare),
    totalFare: toPosNumber(dealFare.totalFare),
    currency: fullDeal?.fareDetails?.currency ?? dealFare.currency ?? null,
    source: "dealFare",
    usedRoomMatrix: false,
    isMultiRoom: slots.length > 1,
    composeDealRef: null,
    rooms: [],
    hotelId: hotelIdFromDeal(fullDeal),
    rawRow: null,
  };
}

function canonicalFromRow(row, { source, slotIndex, fullDeal, usedRoomMatrix, isMultiRoom }) {
  const roomTypeName = rowRoomTypeDisplay(row);
  return {
    seqNum: pickDealSeq(row),
    roomType: roomTypeName,
    roomTypeName,
    board: rowBoard(row) || normalizeSearchCardBoard(fullDeal?.hotel?.board),
    roomSet: rowRoomSet(row) || null,
    adultFare: rowAdultFare(row),
    totalFare: toPosNumber(row?.totalFare ?? row?.TotalFare),
    currency: fullDeal?.fareDetails?.currency ?? row?.currency ?? null,
    source,
    usedRoomMatrix,
    isMultiRoom,
    slotIndex,
    rawRow: row,
    hotelId: hotelIdFromDeal(fullDeal),
  };
}

function toComposeRow(canonical) {
  if (canonical?.rawRow && typeof canonical.rawRow === "object") return canonical.rawRow;
  if (!canonical) return null;
  return {
    "@seqNum": canonical.seqNum,
    seqNum: canonical.seqNum,
    roomSet: canonical.roomSet,
    roomType: canonical.roomType,
    roomTypeName: canonical.roomTypeName,
    board: canonical.board,
    adultFare: canonical.adultFare,
    totalFare: canonical.totalFare,
  };
}

/**
 * @param {{
 *   fullDeal?: object|null,
 *   shortenedDeal?: unknown,
 *   extendedDeal?: unknown,
 *   occupancyRooms?: Array,
 * }} input
 */
export function resolveSearchCardRoom({
  fullDeal = null,
  shortenedDeal = null,
  extendedDeal = null,
  occupancyRooms = null,
} = {}) {
  const deal = fullDeal && typeof fullDeal === "object" ? fullDeal : null;
  const slots = normalizeOccupancyRooms(occupancyRooms);
  if (!deal) {
    return {
      seqNum: null,
      roomType: null,
      roomTypeName: null,
      board: null,
      roomSet: null,
      adultFare: null,
      totalFare: null,
      currency: null,
      source: "missing_deal",
      usedRoomMatrix: false,
      isMultiRoom: false,
      composeDealRef: null,
      rooms: [],
      hotelId: null,
      rawRow: null,
    };
  }

  const matrixRows = collectMatrixRows({
    fullDeal: deal,
    shortenedDeal,
    extendedDeal,
  });
  const canUseMatrix = canSafelyPriceFromRoomMatrix(matrixRows, slots);

  if (!canUseMatrix) {
    return fallbackFromDealFare(deal, slots);
  }

  const source =
    normalizeLdsExtendedDealRows(shortenedDeal).length > 0
      ? "shortenedDeal"
      : normalizeLdsExtendedDealRows(extendedDeal).length > 0
        ? "extendedDeal"
        : "dealMatrix";

  const perSlot = slots.map((slot, slotIndex) => {
    // Each occupancy slot independently gets its cheapest compatible SKU.
    // Two A2 rooms both take cheapest A2 — never a leftover A1 seq.
    const row = pickCheapestCompatibleRow(matrixRows, slot);
    return row
      ? canonicalFromRow(row, {
          source,
          slotIndex,
          fullDeal: deal,
          usedRoomMatrix: true,
          isMultiRoom: slots.length > 1,
        })
      : null;
  });

  if (perSlot.some((item) => !item)) {
    return fallbackFromDealFare(deal, slots);
  }

  const primary = perSlot[0];
  const composeDealRef = perSlot.map(toComposeRow).filter(Boolean);
  return {
    ...primary,
    usedRoomMatrix: true,
    isMultiRoom: slots.length > 1,
    composeDealRef,
    rooms: perSlot,
    hotelId: hotelIdFromDeal(deal),
  };
}

export function compactCanonicalSearchCardRoom(resolved) {
  if (!resolved || typeof resolved !== "object") return null;
  const compactOne = (room) => {
    if (!room || typeof room !== "object") return null;
    return {
      seqNum: room.seqNum ?? null,
      roomType: room.roomType ?? null,
      roomTypeName: room.roomTypeName ?? room.roomType ?? null,
      board: room.board ?? null,
      roomSet: room.roomSet ?? null,
      adultFare: room.adultFare ?? null,
      totalFare: room.totalFare ?? null,
      currency: room.currency ?? null,
      source: room.source ?? null,
      slotIndex: room.slotIndex ?? 0,
    };
  };
  const rooms = Array.isArray(resolved.rooms)
    ? resolved.rooms.map(compactOne).filter(Boolean)
    : [];
  return {
    ...compactOne(resolved),
    usedRoomMatrix: Boolean(resolved.usedRoomMatrix),
    isMultiRoom: Boolean(resolved.isMultiRoom),
    hotelId: resolved.hotelId ?? null,
    rooms,
  };
}

export function readCanonicalSearchCardRoom(selectedResult) {
  if (!selectedResult || typeof selectedResult !== "object") return null;
  const direct =
    selectedResult[SEARCH_CARD_ROOM_FIELD] ||
    selectedResult.fullDeal?.[SEARCH_CARD_ROOM_FIELD] ||
    null;
  if (direct && typeof direct === "object") return direct;
  return null;
}

export function hasCanonicalSearchCardRoom(selectedResult) {
  const room = readCanonicalSearchCardRoom(selectedResult);
  if (!room || typeof room !== "object") return false;
  return Boolean(
    room.seqNum ||
      room.roomType ||
      room.roomTypeName ||
      room.roomSet ||
      (room.adultFare != null && Number(room.adultFare) > 0)
  );
}

export function canonicalRoomToSelectedRoom(canonical) {
  if (!canonical || typeof canonical !== "object") return null;
  const seq = canonical.seqNum ?? null;
  return {
    "@seqNum": seq,
    seqNum: seq,
    roomId: seq,
    RoomId: seq,
    roomType: canonical.roomType ?? canonical.roomTypeName ?? null,
    roomTypeName: canonical.roomTypeName ?? canonical.roomType ?? null,
    roomName: canonical.roomTypeName ?? canonical.roomType ?? null,
    roomSet: canonical.roomSet ?? null,
    RoomSet: canonical.roomSet ?? null,
    board: canonical.board ?? null,
    adultFare: canonical.adultFare ?? null,
    totalFare: canonical.totalFare ?? null,
    searchCardRoomSource: canonical.source ?? null,
  };
}

export function searchCardRoomDisplayName(canonical) {
  if (!canonical || typeof canonical !== "object") return "";
  return String(canonical.roomTypeName || canonical.roomType || "").trim();
}

export function formatSearchOccupancySlotLabel(slot) {
  if (!slot || typeof slot !== "object") return "";
  const adults = Math.max(0, Math.floor(Number(slot.adults) || 0));
  const kids = Math.max(0, Math.floor(Number(slot.kids) || 0));
  const infants = Math.max(0, Math.floor(Number(slot.infants) || 0));
  const parts = [];
  if (adults > 0) parts.push(`${adults} ${adults === 1 ? "מבוגר" : "מבוגרים"}`);
  if (kids > 0) parts.push(`${kids} ${kids === 1 ? "ילד" : "ילדים"}`);
  if (infants > 0) parts.push(`${infants} ${infants === 1 ? "תינוק" : "תינוקות"}`);
  return parts.join(" + ");
}

/**
 * One display line per occupancy slot so a 2-room search shows each composition
 * its cheapest compatible room, not only the primary SKU.
 */
export function searchCardRoomDisplayLines(canonical, occupancyRooms = null) {
  const slots = normalizeOccupancyRooms(occupancyRooms);
  const rooms =
    Array.isArray(canonical?.rooms) && canonical.rooms.length > 0
      ? canonical.rooms
      : canonical
        ? [canonical]
        : [];
  const multi =
    slots.length > 1 || rooms.length > 1 || Boolean(canonical?.isMultiRoom);
  return rooms
    .map((room, i) => {
      const name = searchCardRoomDisplayName(room);
      if (!name) return null;
      if (!multi) return name;
      const slot = slots[room.slotIndex ?? i] || slots[i];
      const occ = formatSearchOccupancySlotLabel(slot);
      return occ ? `חדר ${i + 1} · ${occ}: ${name}` : `חדר ${i + 1}: ${name}`;
    })
    .filter(Boolean);
}

/** Per-slot search-card identity. Never reuse slot 0's unique seq on another slot. */
export function canonicalSearchCardRoomForSlot(canonical, slotIndex = 0) {
  if (!canonical || typeof canonical !== "object") return null;
  const idx = Math.max(0, Math.floor(Number(slotIndex) || 0));
  if (Array.isArray(canonical.rooms) && canonical.rooms.length > 0) {
    const slotted = canonical.rooms[idx];
    return slotted && typeof slotted === "object" ? slotted : null;
  }
  if (idx === 0) return canonical;
  return {
    ...canonical,
    seqNum: null,
    slotIndex: idx,
  };
}

function suggestionFromAvailableRow(row) {
  if (!row || typeof row !== "object") return null;
  return row.roomSuggestion && typeof row.roomSuggestion === "object"
    ? row.roomSuggestion
    : row;
}

/**
 * Re-find the search-card SKU inside a RoomsSelector price-table row list.
 * Never falls back to cheapest.
 */
export function matchCanonicalRoomInAvailableRows(
  availableRooms,
  canonical,
  occupancySlot = null
) {
  if (!canonical || typeof canonical !== "object") return null;
  if (!Array.isArray(availableRooms) || availableRooms.length === 0) return null;

  const suggestion = (row) => suggestionFromAvailableRow(row);
  const pool = occupancySlot
    ? filterAvailableRoomsForSlotOccupancy(availableRooms, occupancySlot)
    : availableRooms;
  if (pool.length === 0) return null;

  if (canonical.seqNum != null && String(canonical.seqNum).trim() !== "") {
    const seqKey = String(canonical.seqNum).trim();
    const bySeq = pool.find((row) => {
      const seq = pickDealSeq(suggestion(row));
      return seq != null && String(seq).trim() === seqKey;
    });
    if (bySeq) {
      if (
        occupancySlot &&
        !isRoomRowOccupancyCompatible(suggestion(bySeq), occupancySlot)
      ) {
        return null;
      }
      return bySeq;
    }
  }

  const wantType = normalizeRoomTypeLabel({
    roomType: canonical.roomType ?? canonical.roomTypeName,
  });
  const wantBoard = normalizeSearchCardBoard(canonical.board);
  const wantSet = canonical.roomSet
    ? String(canonical.roomSet).trim().toUpperCase()
    : "";

  const typed = pool.filter((row) => {
    const s = suggestion(row);
    if (!s) return false;
    if (occupancySlot && !isRoomRowOccupancyCompatible(s, occupancySlot)) {
      return false;
    }
    if (wantType) {
      const gotType = normalizeRoomTypeLabel(s);
      if (gotType !== wantType) return false;
    } else {
      return false;
    }
    if (wantSet) {
      const gotSet = rowRoomSet(s).toUpperCase();
      if (gotSet !== wantSet) return false;
    }
    if (wantBoard) {
      const gotBoard = rowBoard(s);
      if (gotBoard && gotBoard !== wantBoard) return false;
    }
    return true;
  });

  if (typed.length === 1) return typed[0];

  const wantFare = toPosNumber(canonical.adultFare);
  if (wantFare != null) {
    const farePool = typed.length > 0 ? typed : pool;
    const byFare = farePool.filter((row) => {
      const s = suggestion(row);
      if (occupancySlot && !isRoomRowOccupancyCompatible(s, occupancySlot)) {
        return false;
      }
      return toPosNumber(s?.adultFare ?? s?.AdultFare) === wantFare;
    });
    if (byFare.length === 1) return byFare[0];
  }

  return null;
}

export function pricingSnapshotMatchesCanonical(snapshot, canonical) {
  if (!snapshot || typeof snapshot !== "object" || !canonical) return false;
  const snapSeq =
    snapshot.dealSeq ??
    snapshot.seqNum ??
    snapshot["@seqNum"] ??
    snapshot.roomId ??
    snapshot.selectedRoomId ??
    null;
  if (canonical.seqNum && snapSeq && String(snapSeq).trim() === String(canonical.seqNum).trim()) {
    return true;
  }
  const snapType = normalizeRoomTypeLabel(snapshot);
  const canonType = normalizeRoomTypeLabel({
    roomType: canonical.roomType ?? canonical.roomTypeName,
  });
  if (snapType && canonType && snapType === canonType) {
    const snapSet = snapshot.roomSet || snapshot.RoomSet;
    if (!canonical.roomSet || !snapSet || String(snapSet) === String(canonical.roomSet)) {
      return true;
    }
  }
  return false;
}

/** Deal-level snapshot is the LDS search SKU. Do not apply it to a different matrix room. */
export function shouldUseDealPricingSnapshot(resolution, snapshot) {
  if (!snapshot || typeof snapshot !== "object") return false;
  if (!resolution?.usedRoomMatrix) return true;
  return pricingSnapshotMatchesCanonical(snapshot, resolution);
}

export function copyComposeDealRefStamp(from, to) {
  if (!from || !to || from === to) return to;
  if (!Object.prototype.hasOwnProperty.call(from, "__hrComposeDealRefPayload")) {
    return to;
  }
  try {
    Object.defineProperty(to, "__hrComposeDealRefPayload", {
      value: from.__hrComposeDealRefPayload,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    try {
      to.__hrComposeDealRefPayload = from.__hrComposeDealRefPayload;
    } catch {
      /* ignore */
    }
  }
  return to;
}

export function stampComposeDealRef(deal, composeRef) {
  if (!deal || typeof deal !== "object") return deal;
  try {
    Reflect.deleteProperty(deal, "__hrComposeDealRefPayload");
    Object.defineProperty(deal, "__hrComposeDealRefPayload", {
      value: composeRef,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    try {
      deal.__hrComposeDealRefPayload = composeRef;
    } catch {
      /* ignore */
    }
  }
  return deal;
}

export function attachCanonicalSearchCardRoom(deal, resolved) {
  if (!deal || typeof deal !== "object") return deal;
  const compact = compactCanonicalSearchCardRoom(resolved);
  if (!compact) return deal;
  const selectedRoom = canonicalRoomToSelectedRoom(compact);
  const next = {
    ...deal,
    [SEARCH_CARD_ROOM_FIELD]: compact,
    selectedRoom,
  };
  copyComposeDealRefStamp(deal, next);
  if (Array.isArray(resolved?.composeDealRef) || resolved?.composeDealRef === null) {
    stampComposeDealRef(next, resolved.composeDealRef);
  }
  return next;
}

export function mergeCanonicalRoomIntoSelectedResult(nextSelected, occupancyRooms) {
  if (!nextSelected || typeof nextSelected !== "object") return nextSelected;
  const existing = readCanonicalSearchCardRoom(nextSelected);
  if (!existing || !hasCanonicalSearchCardRoom({ [SEARCH_CARD_ROOM_FIELD]: existing })) {
    // Do not invent a search-card SKU on generic profile reset. That would
    // auto-select a room in vacation-package flows that must stay empty, and
    // would disable organized-tour cheapest soft-fill. Identity is stamped
    // on package-card click via attachCanonicalSearchCardRoom.
    return nextSelected;
  }
  const compact = compactCanonicalSearchCardRoom(existing);
  if (!compact || !hasCanonicalSearchCardRoom({ [SEARCH_CARD_ROOM_FIELD]: compact })) {
    return nextSelected;
  }
  const selectedRoom = canonicalRoomToSelectedRoom(compact);
  const occupancyOk = selectedRoomMatchesSearchOccupancy(
    selectedRoom,
    occupancyRooms,
    getRoomsSetByCount
  );
  const next = {
    ...nextSelected,
    [SEARCH_CARD_ROOM_FIELD]: compact,
    selectedRoom: occupancyOk ? selectedRoom : nextSelected.selectedRoom ?? null,
  };
  copyComposeDealRefStamp(nextSelected, next);
  if (nextSelected.fullDeal && typeof nextSelected.fullDeal === "object") {
    next.fullDeal = {
      ...nextSelected.fullDeal,
      [SEARCH_CARD_ROOM_FIELD]: compact,
      selectedRoom: occupancyOk ? selectedRoom : nextSelected.fullDeal.selectedRoom ?? null,
    };
    copyComposeDealRefStamp(nextSelected.fullDeal, next.fullDeal);
  }
  return next;
}

function isDevTraceEnabled() {
  const isDev =
    (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
    (typeof process !== "undefined" && process.env?.NODE_ENV === "development");
  const isTest = typeof process !== "undefined" && process.env?.NODE_ENV === "test";
  return Boolean(isDev) && !isTest;
}

export function buildSearchCardRoomTracePayload(canonical, extras = {}) {
  return {
    hotelId: canonical?.hotelId ?? extras.hotelId ?? null,
    seqNum: canonical?.seqNum ?? null,
    roomType: canonical?.roomType ?? canonical?.roomTypeName ?? null,
    board: canonical?.board ?? null,
    roomSet: canonical?.roomSet ?? null,
    adultFare: canonical?.adultFare ?? null,
    calculatedDisplayedPrice: extras.calculatedDisplayedPrice ?? null,
    source: canonical?.source ?? null,
    usedRoomMatrix: Boolean(canonical?.usedRoomMatrix),
  };
}

export function emitSearchCardRoomTrace(canonical, extras = {}) {
  if (!isDevTraceEnabled()) return;
  try {
    console.info("[SEARCH_CARD_ROOM_TRACE]", buildSearchCardRoomTracePayload(canonical, extras));
  } catch {
    /* ignore */
  }
}

export function emitProfileInitialRoomTrace(canonical, extras = {}) {
  if (!isDevTraceEnabled()) return;
  try {
    console.info("[PROFILE_INITIAL_ROOM_TRACE]", {
      hotelId: extras.hotelId ?? canonical?.hotelId ?? null,
      seqNum: extras.seqNum ?? canonical?.seqNum ?? null,
      roomType: extras.roomType ?? canonical?.roomType ?? canonical?.roomTypeName ?? null,
      board: extras.board ?? canonical?.board ?? null,
      roomSet: extras.roomSet ?? canonical?.roomSet ?? null,
      adultFare: extras.adultFare ?? canonical?.adultFare ?? null,
      calculatedDisplayedPrice: extras.calculatedDisplayedPrice ?? null,
      source: canonical?.source ?? null,
      reason: extras.reason ?? null,
    });
  } catch {
    /* ignore */
  }
}
